import Alert from "@mui/material/Alert"
import Button from "@mui/material/Button"
import Checkbox from "@mui/material/Checkbox"
import Dialog from "@mui/material/Dialog"
import DialogActions from "@mui/material/DialogActions"
import DialogContent from "@mui/material/DialogContent"
import DialogTitle from "@mui/material/DialogTitle"
import FormControlLabel from "@mui/material/FormControlLabel"
import MenuItem from "@mui/material/MenuItem"
import TextField from "@mui/material/TextField"
import Typography from "@mui/material/Typography"
import PropTypes from "prop-types"
import { useEffect, useState } from "react"

import Column from "../../../common/components/Column"
import { podsApi } from "../axios"

export default function AdminUserEditDialog({
  open,
  user,
  canEditStaffFlags,
  podDialerOn = true,
  onClose,
  onSave,
  saving,
}) {
  const [form, setForm] = useState({
    name: "",
    is_active: true,
    is_concierge: false,
    is_onebase_staff: false,
    is_staff: false,
    is_superuser: false,
    is_sms_only: false,
    pod_ids: [],
  })
  const [error, setError] = useState(null)
  const [pods, setPods] = useState([])

  useEffect(() => {
    if (!open) return
    setError(null)
    if (!podDialerOn) return
    podsApi
      .list()
      .then(({ data }) => setPods(data || []))
      .catch(() => setPods([]))
  }, [open, podDialerOn])

  useEffect(() => {
    if (!user) return
    setForm({
      name: user.name || "",
      is_active: Boolean(user.is_active),
      is_concierge: Boolean(user.is_concierge),
      is_onebase_staff: Boolean(user.is_onebase_staff),
      is_staff: Boolean(user.is_staff),
      is_superuser: Boolean(user.is_superuser),
      is_sms_only: Boolean(user.is_sms_only),
      pod_ids: (user.pods || []).map((pod) => pod.id),
    })
  }, [user])

  function handleSubmit(event) {
    event.preventDefault()
    setError(null)
    Promise.resolve(onSave(form)).catch((err) => {
      const data = err?.response?.data
      if (typeof data === "string") {
        setError(data)
      } else if (data && typeof data === "object") {
        const messages = Object.entries(data).flatMap(([field, value]) => {
          const items = Array.isArray(value) ? value : [value]
          return items.map((item) =>
            field === "non_field_errors" ? String(item) : `${field}: ${item}`
          )
        })
        setError(messages.join(" ") || "Could not update user.")
      } else {
        setError("Could not update user.")
      }
    })
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <form onSubmit={handleSubmit}>
        <DialogTitle>Edit user</DialogTitle>
        <DialogContent>
          <Column gap={2} sx={{ pt: 1 }}>
            {error ? <Alert severity="error">{error}</Alert> : null}
            <TextField
              label="Email"
              value={user?.email || ""}
              size="small"
              fullWidth
              disabled
            />
            <TextField
              label="Name"
              value={form.name}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, name: e.target.value }))
              }
              size="small"
              fullWidth
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={form.is_active}
                  onChange={(e) =>
                    setForm((prev) => ({
                      ...prev,
                      is_active: e.target.checked,
                    }))
                  }
                />
              }
              label="Active"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={form.is_concierge}
                  onChange={(e) =>
                    setForm((prev) => ({
                      ...prev,
                      is_concierge: e.target.checked,
                    }))
                  }
                />
              }
              label="Concierge"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={form.is_onebase_staff}
                  onChange={(e) =>
                    setForm((prev) => ({
                      ...prev,
                      is_onebase_staff: e.target.checked,
                    }))
                  }
                />
              }
              label="OneBase staff"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={form.is_sms_only}
                  onChange={(e) =>
                    setForm((prev) => ({
                      ...prev,
                      is_sms_only: e.target.checked,
                      pod_ids: e.target.checked ? prev.pod_ids : [],
                    }))
                  }
                />
              }
              label="Texting only (texts and pod dialer, nothing else)"
            />
            {form.is_sms_only && podDialerOn ? (
              <TextField
                select
                size="small"
                label="Pods"
                value={form.pod_ids}
                SelectProps={{
                  multiple: true,
                  renderValue: (ids) =>
                    pods
                      .filter((pod) => ids.includes(pod.id))
                      .map((pod) => pod.name)
                      .join(", "),
                }}
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, pod_ids: e.target.value }))
                }
                helperText="Pods this employee dials for."
              >
                {pods.map((pod) => (
                  <MenuItem key={pod.id} value={pod.id}>
                    <Checkbox
                      size="small"
                      checked={form.pod_ids.includes(pod.id)}
                    />
                    {pod.name}
                  </MenuItem>
                ))}
              </TextField>
            ) : null}
            {canEditStaffFlags ? (
              <>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={form.is_staff}
                      onChange={(e) =>
                        setForm((prev) => ({
                          ...prev,
                          is_staff: e.target.checked,
                        }))
                      }
                    />
                  }
                  label="Staff"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={form.is_superuser}
                      onChange={(e) =>
                        setForm((prev) => ({
                          ...prev,
                          is_superuser: e.target.checked,
                        }))
                      }
                    />
                  }
                  label="Superuser"
                />
              </>
            ) : null}
            <Typography variant="caption" color="text.secondary">
              Deactivating a user is available from the row delete action (same
              as Django admin deactivate).
            </Typography>
          </Column>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button type="submit" variant="contained" disabled={saving}>
            Save
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  )
}

AdminUserEditDialog.propTypes = {
  podDialerOn: PropTypes.bool,
  open: PropTypes.bool.isRequired,
  user: PropTypes.object,
  canEditStaffFlags: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  saving: PropTypes.bool,
}
